import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { createRoot, type Root } from 'react-dom/client';
import { createInlineMarkupRegistry, type InlineMarkupRegistry } from '../inline-markup-registry';
import type { InlineRenderCtx } from '../inline-markup-types';

const SKIP_MARKS = new Set(['code', 'link']);

export const inlineMarkupPluginKey = new PluginKey<DecorationSet>('inlineMarkup');

type InlineMarkupOptions = {
  registry: InlineMarkupRegistry;
};

// Visits the doc's inline text, skipping code and links — where a token is literal, not markup.
export const eachInlineTextNode = (doc: ProseMirrorNode, visit: (text: string, position: number) => void) => {
  doc.descendants((node, position) => {
    if (node.type.name === 'codeBlock') return false;
    if (!node.isText || !node.text || node.marks.some(mark => SKIP_MARKS.has(mark.type.name))) return;
    visit(node.text, position);
  });
};

// Each prose widget hosts the kind's one React chip in its own root; the map lets `destroy` unmount it.
const widgetRoots = new WeakMap<Node, Root>();

const buildInlineDecorations = (doc: ProseMirrorNode, registry: InlineMarkupRegistry) => {
  const decorations: Decoration[] = [];
  eachInlineTextNode(doc, (text, position) => {
    for (const { index, raw, token, entry } of registry.parseTokens(text)) {
      const from = position + index;
      const to = from + raw.length;
      const ctx: InlineRenderCtx = { raw };
      // Every token renders as a widget: the visible chip/pill sits over the raw token, which stays in
      // the doc, hidden and non-editable, so it survives save.
      decorations.push(Decoration.inline(from, to, { style: 'display: none', contenteditable: 'false' }));
      decorations.push(
        Decoration.widget(
          from,
          () => {
            const el = document.createElement('span');
            const root = createRoot(el);
            widgetRoots.set(el, root);
            root.render(entry.renderReact(token as never, ctx));
            return el;
          },
          {
            side: -1,
            key: `inline:${token.kind}:${from}:${raw}:${entry.decorationKey?.(token as never) ?? ''}`,
            destroy: node => {
              const root = widgetRoots.get(node);
              if (!root) return;
              widgetRoots.delete(node);
              // Defer: `destroy` can fire inside ProseMirror's update, and unmounting a root then warns.
              queueMicrotask(() => root.unmount());
            },
          },
        ),
      );
    }
  });
  return DecorationSet.create(doc, decorations);
};

// One decoration extension for every registered inline kind. Rebuild on a doc edit, or when a consumer
// signals through the plugin key that what a chip draws from has changed; otherwise remap in place.
export const InlineMarkupExtension = Extension.create<InlineMarkupOptions>({
  name: 'inlineMarkup',

  // No kinds by default — an unconfigured editor leaves every token as the raw text it already is.
  addOptions() {
    return { registry: createInlineMarkupRegistry([]) };
  },

  addProseMirrorPlugins() {
    const { registry } = this.options;
    return [
      new Plugin({
        key: inlineMarkupPluginKey,
        state: {
          init: (_, { doc }) => {
            return buildInlineDecorations(doc, registry);
          },
          apply: (transaction, oldDecorations) => {
            if (transaction.getMeta(inlineMarkupPluginKey) || transaction.docChanged) {
              return buildInlineDecorations(transaction.doc, registry);
            }
            return oldDecorations.map(transaction.mapping, transaction.doc);
          },
        },
        props: {
          decorations(state) {
            return inlineMarkupPluginKey.getState(state);
          },
        },
      }),
    ];
  },
});
